import {
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  KeyIcon,
  Squares2X2Icon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import AIIcon from 'icons/AI';
import APARIcon from 'icons/APARIcon';
import AppsIcon from 'icons/Apps';
import ArrowLeftRight from 'icons/ArrowLeftRight';
import CalendarIcon2 from 'icons/CalendarIcon2';
import ContractorsIcon from 'icons/ContractorsIcon';
import EmailIcon2 from 'icons/Email2';
import Expenses from 'icons/Expenses';
import Finance from 'icons/Finance';
import HomeDashboard from 'icons/HomeDashboard';
import AnalyticsIcon from 'icons/AnalyticsIcon';
import OperatingExpensesIcon from 'icons/OperatingExpensesIcon';
import OverviewIcon from 'icons/OverviewIcon';
import PaymentMethodIcon from 'icons/PaymentMethodIcon';
import Payroll from 'icons/Payroll';
import Gift from 'icons/Gift';
import Products from 'icons/Products';
import QBIntegrationsIcon from 'icons/QBIntegrationsIcon';
import RevenueAnalyticsIcon from 'icons/RevenueAnalyticsIcon';
import SecurityLockIcon from 'icons/SecurityLockIcon';
import Settings from 'icons/Settings';
import SpendTransparent from 'icons/SpendTransparent';
import TeamIcon from 'icons/TeamIcon';
import TeamsIcon from 'icons/TeamsIcon';
import Transactions from 'icons/Transactions';
import { AdminRoutes, AdminRoutesWithAccess, ReferralRoutes } from 'routes';
import { getWhiteLabelTeamTerm, getWhiteLabelTeamTermPlural } from 'utils/whiteLabelConfig';
import { NavItem } from '../types';
import TimeTrackingIcon2 from 'icons/TimeTrackingIcon2';

/**
 * Sidebar rows (the first level of each nav's subNavs) are rendered in order and matched by index,
 * so keep every row `isShownInBar: true`. Nested tabs are exempt: their active state is resolved
 * against the rendered tab list by route, so hidden detail entries can sit anywhere in the array.
 * See ../CLAUDE.md.
 */

export const getManagerNavigation = (): NavItem[] => {
  const teamTerm = getWhiteLabelTeamTerm();
  const teamTermPlural = getWhiteLabelTeamTermPlural();

  return [
    {
      name: 'Home',
      icon: <HomeDashboard />,
      href: AdminRoutes.Dashboard,
      showInMainNav: true,
    },
    {
      name: 'Payroll',
      icon: <Payroll />,
      href: AdminRoutes.PayrollDashboard,
      onlyShownTo: ['managerWithTeams', 'isPayrollEnabled'],
      subNavs: [
        {
          name: 'teamMenu',
          isShownInBar: true,
          isCustomElement: true,
          href: AdminRoutes.PayrollDashboard,
        },
        {
          name: 'Dashboard',
          icon: <OverviewIcon className="h-5 w-5" />,
          href: AdminRoutes.PayrollDashboard,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.PayrollDashboard.access,
          subNavs: [
            {
              name: 'Contracts Pending Signature',
              href: AdminRoutes.PendingSignatures,
              isShownInBar: false,
            },
          ],
        },
        {
          name: teamTerm,
          icon: <TeamIcon />,
          href: AdminRoutes.Team,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.Team.access,
          subNavs: [
            {
              name: 'Add Contractor',
              href: AdminRoutes.AddContractor,
              isShownInBar: false,
            },
            {
              name: 'Add US Employee',
              href: AdminRoutes.AddEmployee,
              isShownInBar: false,
            },
            {
              name: 'Add EOR Employee',
              href: AdminRoutes.AddEOR,
              isShownInBar: false,
            },
            {
              name: 'EOR Employee',
              href: AdminRoutes.EOREmployeeProfileByID,
              isShownInBar: false,
            },
            {
              name: 'Contractor',
              href: AdminRoutes.ContractorProfileByID,
              isShownInBar: false,
            },
            {
              name: 'Employee',
              href: AdminRoutes.EmployeeProfileByID,
              isShownInBar: false,
            },
            {
              name: 'Contractor Contracts',
              href: AdminRoutes.ContractorContractPage,
              isShownInBar: false,
            },
            {
              name: 'Contractor Time Off',
              href: AdminRoutes.ContractorTimeOff,
              isShownInBar: false,
            },
            {
              name: 'US Employee Time Off',
              href: AdminRoutes.EmployeeTimeOff,
              isShownInBar: false,
            },
            {
              name: 'EOR Employments',
              href: AdminRoutes.EOREmployeeEmployment,
              isShownInBar: false,
            },
          ],
        },
        {
          name: 'US Payroll',
          icon: <Payroll />,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.USPayroll.access,
          subNavs: [
            {
              name: 'Overview',
              href: AdminRoutes.USPayroll,
              onlyShownTo: AdminRoutesWithAccess.USPayroll.access,
            },
            {
              name: 'History',
              href: AdminRoutes.PayrollHistory,
              onlyShownTo: AdminRoutesWithAccess.PayrollHistory.access,
            },
            {
              name: 'Benefits',
              href: AdminRoutes.W2Integrations,
              onlyShownTo: AdminRoutesWithAccess.W2Integrations.access,
            },
            {
              name: 'Pay Cycles',
              href: AdminRoutes.USPayrollPaySchedules,
              onlyShownTo: AdminRoutesWithAccess.USPayrollPaySchedules.access,
            },
            {
              name: 'Expenses',
              icon: <Expenses className="h-5 w-5" />,
              href: AdminRoutes.Expenses,
              isShownInBar: true,
              onlyShownTo: AdminRoutesWithAccess.Expenses.access,
              subNavs: [
                {
                  name: 'Review Expenses',
                  href: AdminRoutes.ReviewExpenses,
                  isShownInBar: false,
                },
              ],
            },
            {
              name: 'State Taxes',
              href: AdminRoutes.Taxes,
              onlyShownTo: AdminRoutesWithAccess.Taxes.access,
            },
            {
              name: 'Pay Cycle Details',
              href: AdminRoutes.USPayrollPaySchedulesDetails,
              onlyShownTo: AdminRoutesWithAccess.USPayrollPaySchedulesDetails.access,
              isShownInBar: false,
            },
            {
              name: 'Manage Employees',
              href: AdminRoutes.USPayrollPaySchedulesEmployees,
              onlyShownTo: AdminRoutesWithAccess.USPayrollPaySchedulesEmployees.access,
              isShownInBar: false,
            },
            {
              name: 'Compliance',
              href: AdminRoutes.PayrollCompliance,
              onlyShownTo: AdminRoutesWithAccess.PayrollCompliance.access,
              isShownInBar: false,
            },
            {
              name: "Workers' Comp",
              href: AdminRoutes.WorkersCompCompliance,
              onlyShownTo: AdminRoutesWithAccess.WorkersCompCompliance.access,
              isShownInBar: false,
            },
            {
              name: 'Integration Type',
              href: AdminRoutes.IntegrationTypes,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.IntegrationTypes.access,
            },
            {
              name: 'Deductions',
              href: AdminRoutes.CompanyDeductions,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.CompanyDeductions.access,
            },
            {
              name: 'Deductions',
              href: AdminRoutes.Deductions,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.Deductions.access,
            },
            {
              name: 'Garnishments',
              href: AdminRoutes.Garnishments,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.Garnishments.access,
            },
            {
              name: 'Earnings',
              href: AdminRoutes.Earnings,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.Earnings.access,
            },
            {
              name: 'History',
              href: AdminRoutes.RegularPayrollHistory,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.RegularPayrollHistory.access,
            },
            {
              name: 'Work Locations',
              href: AdminRoutes.WorkLocations,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.WorkLocations.access,
            },
            {
              name: 'Regular Payroll',
              href: AdminRoutes.USPayrollRun,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.USPayrollRun.access,
            },
            {
              name: 'Employee Paystubs',
              href: AdminRoutes.EmployeeChecks,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.EmployeeChecks.access,
            },
            {
              name: 'Pending Paystubs',
              href: AdminRoutes.PendingEmployeeCheck,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.PendingEmployeeCheck.access,
            },
            {
              name: 'Tax Setup',
              href: AdminRoutes.ZeroWageFilings,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.ZeroWageFilings.access,
            },
            {
              name: 'Tax Deposits',
              href: AdminRoutes.TaxDeposits,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.TaxDeposits.access,
            },
            {
              name: 'Reimbursements',
              href: AdminRoutes.EarningTypes,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.EarningTypes.access,
            },
            {
              name: 'Deductions',
              href: AdminRoutes.DeductionTypes,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.DeductionTypes.access,
            },
            {
              name: 'Off-Cycle Payroll',
              href: AdminRoutes.USPayrollRunOffCycle,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.USPayrollRunOffCycle.access,
            },
            {
              name: 'Bonus Payroll',
              href: AdminRoutes.USPayrollRunBonus,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.USPayrollRunBonus.access,
            },
          ],
        },
        {
          name: 'Contractors',
          icon: <ContractorsIcon className="h-6 w-6" />,
          isShownInBar: true,
          subNavs: [
            {
              name: 'Overview',
              href: AdminRoutes.Contractors,
            },
            {
              name: 'History',
              href: AdminRoutes.Invoices,
            },

            {
              name: 'Review',
              href: AdminRoutes.ReviewInvoices,
              isShownInBar: false,
            },
            {
              name: 'Approve',
              href: AdminRoutes.PayApprovedInvoices,
              isShownInBar: false,
            },
          ],
        },
        {
          name: 'EOR',
          icon: <GlobeAltIcon className="h-6 w-6" />,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.EOR.access,
          subNavs: [
            {
              name: 'Overview',
              href: AdminRoutes.EOR,
              onlyShownTo: AdminRoutesWithAccess.EOR.access,
            },
            {
              name: 'History',
              href: AdminRoutes.EORPayrollHistory,
              onlyShownTo: AdminRoutesWithAccess.EORPayrollHistory.access,
            },
          ],
        },
        {
          name: 'Payments',
          icon: <PaymentMethodIcon className="h-6 w-6 [&>path]:stroke-[1.5]" />,
          isShownInBar: true,
          subNavs: [
            { name: 'Overview', href: AdminRoutes.Payments },
            { name: 'Fees', href: AdminRoutes.Fees, onlyShownTo: AdminRoutesWithAccess.Fees.access },
            { name: 'Receipt', href: AdminRoutes.InvoicesPaymentReceipt, isShownInBar: false },
            { name: 'EOR Receipt', href: AdminRoutes.EORPaymentReceipt, isShownInBar: false },
            { name: 'W2 Subscription Receipt', href: AdminRoutes.W2SubscriptionPaymentReceipt, isShownInBar: false },
            { name: 'US Payroll Receipt', href: AdminRoutes.USPayrollPaymentReceipt, isShownInBar: false },
            {
              name: 'Contractor Subscription Receipt',
              href: AdminRoutes.ContractorSubscriptionPaymentReceipt,
              isShownInBar: false,
            },
            { name: 'MISC Receipt', href: AdminRoutes.MiscPaymentReceipt, isShownInBar: false },
          ],
        },
        {
          name: 'Time Off',
          icon: <CalendarIcon2 className="w-5 [&>path]:stroke-[1.5]" />,
          isShownInBar: true,
          subNavs: [
            {
              name: 'Overview',
              href: AdminRoutes.TimeOff,
              isShownInBar: true,
            },
            {
              name: 'Balances',
              href: AdminRoutes.TimeOffBalances,
              isShownInBar: true,
            },
            {
              name: 'Review Time Off',
              href: AdminRoutes.ReviewTimeOffs,
              isShownInBar: false,
            },
          ],
        },
        {
          name: 'Time Tracking',
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.TimeTrackingOverview.access,
          icon: <TimeTrackingIcon2 className="w-6 [&>path]:stroke-[1.8]" />,
          subNavs: [
            {
              name: 'Overview',
              href: AdminRoutes.TimeTrackingOverview,
              isShownInBar: true,
            },
            {
              name: 'Timesheets',
              href: AdminRoutes.ReviewTimesheets,
              isShownInBar: true,
            },
            {
              name: 'Payroll Timesheet Review',
              href: AdminRoutes.TimeTrackingPayrollReview,
              isShownInBar: false,
            },
            {
              name: 'Carryover Timesheets',
              href: AdminRoutes.TimeTrackingCarryover,
              isShownInBar: false,
            },
          ],
        },
        {
          name: 'Documents',
          icon: <DocumentTextIcon className="h-6 w-6" />,
          href: AdminRoutes.Documents,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.Documents.access,
          subNavs: [
            {
              name: 'IRS Filings',
              href: AdminRoutes.IRSFiling,
              onlyShownTo: AdminRoutesWithAccess.IRSFiling.access,
              isShownInBar: false,
            },
          ],
        },
      ],
      showInMainNav: true,
    },
    {
      name: 'Payments',
      href: AdminRoutes.ARInvoices,
      icon: <APARIcon />,
      showInMainNav: true,
      onlyShownTo: AdminRoutesWithAccess.ARInvoices.access,
      subNavs: [
        {
          name: 'Overview',
          icon: <OverviewIcon className="h-5 w-5" />,
          href: AdminRoutes.ARInvoices,
          isShownInBar: true,
          subNavs: [
            {
              name: 'Create Invoice',
              href: AdminRoutes.ARInvoiceCreation,
              isShownInBar: false,
            },
            {
              name: 'Edit Invoice',
              href: AdminRoutes.ARInvoiceEdit,
              isShownInBar: false,
            },
            {
              name: 'Create Recurring Invoice',
              href: AdminRoutes.ARInvoiceTemplateCreation,
              isShownInBar: false,
            },
            {
              name: 'Edit Recurring Invoice',
              href: AdminRoutes.ARInvoiceTemplateEdit,
              isShownInBar: false,
            },
            {
              // Maps the post-enable onboarding page to the Payments nav so the
              // bottom-nav tab highlights while onboarding (not just after).
              name: 'Onboarding',
              href: AdminRoutes.AROnboarding,
              isShownInBar: false,
            },
          ],
        },
        {
          name: 'Transactions',
          icon: <Transactions className="mt-[2px] h-5 w-5 [&>path]:stroke-[1.5px]" />,
          href: AdminRoutes.ARTransactions,
          isShownInBar: true,
        },
        {
          name: 'Contacts',
          icon: <TeamIcon className="h-5 w-5" />,
          href: AdminRoutes.ARContacts,
          isShownInBar: true,
        },
      ],
    },
    {
      name: 'Spend',
      href: AdminRoutes.APBillPay,
      icon: <SpendTransparent className="h-6 w-6" />,
      showInMainNav: true,
      onlyShownTo: AdminRoutesWithAccess.APBillPay.access,
      subNavs: [
        {
          name: 'Overview',
          icon: <OverviewIcon className="h-5 w-5" />,
          href: AdminRoutes.APBillPay,
          isShownInBar: true,
          subNavs: [
            {
              name: 'Create Bill',
              href: AdminRoutes.APBillCreate,
              isShownInBar: false,
            },
            {
              name: 'Edit Bill',
              href: AdminRoutes.APBillEdit,
              isShownInBar: false,
            },
            {
              name: 'Create Recurring Bill',
              href: AdminRoutes.APBillTemplateCreation,
              isShownInBar: false,
            },
            {
              name: 'Edit Recurring Bill',
              href: AdminRoutes.APBillTemplateEdit,
              isShownInBar: false,
            },
          ],
        },
        {
          name: 'Transactions',
          icon: <Transactions className="mt-[2px] h-5 w-5 [&>path]:stroke-[1.5px]" />,
          href: AdminRoutes.APTransactions,
          isShownInBar: true,
        },
        {
          name: 'Contacts',
          icon: <TeamIcon className="h-5 w-5" />,
          href: AdminRoutes.APContacts,
          isShownInBar: true,
        },
        {
          name: 'Cards',
          icon: <PaymentMethodIcon className="mt-0.5 h-5 w-5 [&>path]:stroke-[1.5]" />,
          href: AdminRoutes.APCards,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.APCards.access,
          isBeta: true,
          subNavs: [
            {
              name: 'Card Details',
              href: AdminRoutes.APCardDetails,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.APCardDetails.access,
            },
          ],
        },
      ],
    },
    {
      name: 'Cash',
      href: AdminRoutes.BankingOverview,
      icon: <Finance />,
      showInMainNav: true,
      onlyShownTo: AdminRoutesWithAccess.BankingOverview.access,
      subNavs: [
        {
          name: 'Overview',
          icon: <Squares2X2Icon className="h-5 w-5" />,
          href: AdminRoutes.BankingOverview,
          isShownInBar: true,
        },
        {
          name: 'Transactions',
          icon: <ArrowLeftRight className="mt-0.5 h-5 w-5" />,
          href: AdminRoutes.BankingTransactions,
          isShownInBar: true,
          subNavs: [{ name: 'Transaction Detail', href: AdminRoutes.BankingTransactionDetails, isShownInBar: false }],
        },
        {
          name: 'Banking Onboarding',
          icon: <Products className="h-6 w-6" />,
          href: AdminRoutes.BankingOnboarding,
          isShownInBar: false,
        },
        {
          name: 'Accounts',
          icon: <CurrencyDollarIcon className="h-6 w-6" />,
          href: AdminRoutes.BankingAccounts,
          isShownInBar: false,
        },
        {
          name: 'Recipients',
          icon: <TeamIcon className="h-5 w-5" />,
          href: AdminRoutes.BankingRecipients,
          isShownInBar: false,
        },
      ],
    },
    {
      name: 'Analyst',
      href: AdminRoutes.AI,
      icon: <AIIcon className="h-6 w-6" />,
      showInMainNav: true,
      onlyShownTo: AdminRoutesWithAccess.AI.access,
    },
    {
      name: 'Analytics',
      href: AdminRoutes.Analytics,
      icon: <AnalyticsIcon className="h-6 w-6" />,
      showInMainNav: true,
      onlyShownTo: AdminRoutesWithAccess.Analytics.access,
      subNavs: [
        {
          name: 'Overview',
          icon: <OverviewIcon className="h-5 w-5" />,
          href: AdminRoutes.AnalyticsOverview,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.AnalyticsOverview.access,
        },
        {
          name: 'Revenue Analytics',
          icon: <RevenueAnalyticsIcon className="h-5 w-5" />,
          href: AdminRoutes.AnalyticsRevenue,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.AnalyticsRevenue.access,
        },
        {
          name: 'Operating Expenses',
          icon: <OperatingExpensesIcon className="mt-0.5 h-5 w-5" />,
          href: AdminRoutes.AnalyticsExpenses,
          isShownInBar: true,
          onlyShownTo: AdminRoutesWithAccess.AnalyticsExpenses.access,
        },
      ],
    },
    {
      name: 'Apps',
      href: AdminRoutes.Apps,
      icon: <AppsIcon />,
      showInMainNav: true,
      onlyShownTo: AdminRoutesWithAccess.Apps.access,
    },

    {
      name: '',
      icon: <Settings />,
      href: AdminRoutes.AccountPreferences,
      subNavs: [
        {
          name: 'company',
          href: '#',
          isShownInBar: true,
          category: 'Company',
          subNavs: [
            {
              name: 'General',
              icon: <BuildingOffice2Icon className="ml-0.5 h-5 w-5" />,
              href: AdminRoutes.AccountPreferences,
              isShownInBar: true,
            },
            {
              name: 'Roles',
              icon: <UsersIcon className="ml-0.5 h-5 w-5" />,
              href: AdminRoutes.Roles,
              isShownInBar: true,
              onlyShownTo: AdminRoutesWithAccess.Roles.access,
            },
            {
              name: 'API Keys',
              icon: <KeyIcon className="ml-0.5 h-5 w-5" />,
              href: AdminRoutes.ApiKeys,
              isShownInBar: true,
              onlyShownTo: AdminRoutesWithAccess.ApiKeys.access,
            },
            {
              name: 'Activity Log',
              icon: <ClipboardDocumentListIcon className="ml-0.5 h-5 w-5" />,
              href: AdminRoutes.ActivityLog,
              isShownInBar: true,
              onlyShownTo: AdminRoutesWithAccess.ActivityLog.access,
            },
            {
              name: 'Payment Methods',
              href: AdminRoutes.PaymentMethods,
              isShownInBar: true,
              icon: <PaymentMethodIcon className="" />,
            },
            {
              name: 'Companies',
              href: AdminRoutes.WhiteLabelCompanies,
              isShownInBar: true,
              icon: <BuildingOffice2Icon className="ml-0.5 h-5 w-5" />,
              onlyShownTo: AdminRoutesWithAccess.WhiteLabelCompanies.access,
            },
          ],
        },
        {
          name: 'personal',
          href: '#',
          isShownInBar: true,
          category: 'Personal',
          subNavs: [
            {
              name: 'Profile',
              icon: <UserIcon className="mt-0.5 h-5 w-5" />,
              href: AdminRoutes.ProfileSettings,
              isShownInBar: true,
            },
            {
              name: 'Security',
              icon: <SecurityLockIcon className="ml-[1px] mt-0.5 h-5 w-5" />,
              href: AdminRoutes.SecuritySettings,
              isShownInBar: true,
            },
            {
              name: 'Notifications',
              icon: <EmailIcon2 className="h-6 w-6" />,
              href: AdminRoutes.Notifications,
              isShownInBar: true,
              onlyShownTo: AdminRoutesWithAccess.Notifications.access,
            },
            {
              name: 'Referrals',
              icon: <Gift className="mt-0.5 h-5 w-5" />,
              href: ReferralRoutes.Portal,
              isShownInBar: true,
            },
          ],
        },
        {
          name: 'payroll',
          href: '#',
          isShownInBar: true,
          category: 'Payroll',
          onlyShownTo: AdminRoutesWithAccess.Team.access,
          subNavs: [
            {
              name: teamTermPlural,
              icon: <TeamsIcon className="mt-1" />,
              href: AdminRoutes.Teams,
              isShownInBar: true,
            },
            {
              name: 'Time Off Policies',
              href: AdminRoutes.TimeOffPolicy,
              isShownInBar: true,
              icon: <CalendarIcon2 className="ml-0.5 h-5 w-5" />,
            },
            {
              name: 'Time Tracking',
              href: AdminRoutes.TimeTrackingSettings,
              isShownInBar: true,
              onlyShownTo: AdminRoutesWithAccess.TimeTrackingSettings.access,
              icon: <TimeTrackingIcon2 className="ml-0.5 h-5 w-5" />,
            },
            {
              name: 'Payout Analytics',
              href: AdminRoutes.CorridorRates,
              isShownInBar: true,
              onlyShownTo: {
                anyOf: [
                  ...AdminRoutesWithAccess.CorridorRates.access,
                  ...AdminRoutesWithAccess.PayoutVolume.access,
                  ...AdminRoutesWithAccess.AchExposure.access,
                ],
              },
              icon: <AnalyticsIcon className="ml-0.5 h-5 w-5" />,
            },
            {
              name: 'Payout Analytics',
              href: AdminRoutes.PayoutVolume,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.PayoutVolume.access,
              icon: <AnalyticsIcon className="ml-0.5 h-5 w-5" />,
            },
            {
              name: 'Payout Analytics',
              href: AdminRoutes.AchExposure,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.AchExposure.access,
              icon: <AnalyticsIcon className="ml-0.5 h-5 w-5" />,
            },
            {
              name: 'Integrations',
              href: AdminRoutes.Integrations,
              isShownInBar: true,
              icon: <QBIntegrationsIcon className="ml-1 mt-[5.2px] h-4 w-4" />,
              onlyShownTo: AdminRoutesWithAccess.Integrations.access,
            },
            {
              name: 'Integrations',
              href: AdminRoutes.QBIntegrationsConnect,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.QBIntegrationsConnect.access,
            },
            {
              name: 'Google Calendar',
              href: AdminRoutes.GoogleCalendar,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.GoogleCalendar.access,
            },
            {
              name: 'QuickBooks',
              href: AdminRoutes.QBIntegrations,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.QBIntegrations.access,
            },
            {
              name: 'QuickBooks US',
              href: AdminRoutes.QBIntegrationsUSPayroll,
              isShownInBar: false,
              onlyShownTo: AdminRoutesWithAccess.QBIntegrationsUSPayroll.access,
            },
            {
              name: 'Time Off Policy Details',
              href: AdminRoutes.TimeOffPolicyByID,
              isShownInBar: false,
            },
          ],
        },
      ],
      showInMainNav: false,
      isTabbedQuery: true,
    },
  ];
};
